import { BookingState, Gym, ILogger } from 'fitbeat.models';
import moment from 'moment-timezone';
import {
  UserProfile,
  UserWorkoutSessionWithDisplayInfo,
  UserWorkoutStatus,
} from '../../../entities';
import { Mapper } from '../../serialization/mapper';
import {
  IUserProfileRepository,
  IWorkoutWithDisplayInfoRepository,
} from '../interfaces';
import { DbWorkoutRepository } from './db-workout-repository';
import { PouchDbQueryHelper } from './pouchdb-query-helper';

export class DbWorkoutWithDisplayInfoRepository
  implements IWorkoutWithDisplayInfoRepository {
  private db: PouchDbQueryHelper;
  // use any for db, since it is either pouchdb-node or pouchdb
  constructor(
    private gymStateDb: any,
    private userRepo: IUserProfileRepository,
    private logger: ILogger,
  ) {
    this.db = new PouchDbQueryHelper(gymStateDb, 'DbWorkoutWithDisplayInfoRepository', this.logger);
  }

  public async getAll(): Promise<UserWorkoutSessionWithDisplayInfo[]> {
    const results: any = await this.gymStateDb.allDocs({ include_docs: true });
    const docs: any[] = results.rows
      .map((row: any) => row.doc)
      .filter((doc: any) => doc._id !== '0');
    return this.mapToUserWorkoutSessionWithDisplayInfo(docs);
  }

  public async get(
    gym: Gym,
    from?: Date,
    to?: Date ): Promise<UserWorkoutSessionWithDisplayInfo[]> {
    const queryOptions: any = {
      selector: {
        startTime: {
          $gte: from,
          $lte: to,
        },
        gymName: gym.name,
      },
      limit: Number.MAX_SAFE_INTEGER,
    };
    const results: any = await this.gymStateDb.find(queryOptions);
    const docs: any[] = results.docs.filter((doc: any) => doc._id !== '0');
    return this.mapToUserWorkoutSessionWithDisplayInfo(docs);
  }

  public async getFutureBookingsForPtDashboard(
    startingFrom: Date,
    endingAt: Date,
    gymName: string ): Promise<any[]> {
    const queryOptions = {
      limit: 10000,
      selector: {
        startTime: {
          $gte: startingFrom,
          $lte: endingAt,
        },
        removedAt: false,
        gymName,
        anonymised: false,
        bookingState: BookingState.ConfirmedBooking,
      },
    };

    const logParams = `from: ${startingFrom}`;
    const resultDocs = await this.db.queryAndGetDocs(
      `getFutureBookingsForPtDashboard(${logParams})`,
      queryOptions);
    const results = this.mapToUserWorkoutSessionWithDisplayInfo(resultDocs, false);
    return results;
  }

  private async mapToUserWorkoutSessionWithDisplayInfo(
    sessions: UserWorkoutSessionWithDisplayInfo[],
    withSetup: boolean = true): Promise<UserWorkoutSessionWithDisplayInfo[]> {

      const transformedSessions = this.transformSessions(sessions);
      if (withSetup) {
        Mapper.setupAllAfterDeserialize(transformedSessions);
      }
      await this.loadDisplayInfoIfAvailable(transformedSessions);
      return transformedSessions;
  }

  private transformSessions(sessions: UserWorkoutSessionWithDisplayInfo[]): UserWorkoutSessionWithDisplayInfo[]  {
    const transformedSessions = sessions.map(
      (session: any): UserWorkoutSessionWithDisplayInfo => {
        const returnObj: UserWorkoutSessionWithDisplayInfo = Object.assign(
          new UserWorkoutSessionWithDisplayInfo(
            session.name,
            session.amountOfStagesIncludingWarmupAndWarmdown),
          session);
        DbWorkoutRepository.buildDates(returnObj);
        return returnObj;
      });
    return transformedSessions;
  }

  private async loadDisplayInfoIfAvailable(
    userWorkoutSessionWithDisplayInfos: UserWorkoutSessionWithDisplayInfo[] ) {
    if (!this.userRepo) {
      return;
    }

    const uniqueUserProfileIds = Array.from(
      new Set(
        userWorkoutSessionWithDisplayInfos
          .filter((userWorkoutSessionWithDisplayInfo) => {
            const { userProfileId } = userWorkoutSessionWithDisplayInfo;
            return userProfileId && userProfileId !== '';
          })
          .map((userWorkoutSessionWithDisplayInfo) => {
            const { userProfileId } = userWorkoutSessionWithDisplayInfo;
            return userProfileId;
          })));

    if (uniqueUserProfileIds.length === 0) {
      return;
    }

    const userProfileMap = {};
    const allUsers = await this.userRepo.getUsersByIds(uniqueUserProfileIds);
    allUsers.forEach((user: UserProfile) => {
      userProfileMap[user.uid] = user;
    });

    userWorkoutSessionWithDisplayInfos.map(
      (userWorkoutSessionWithDisplayInfo) => {
        const { userProfileId } = userWorkoutSessionWithDisplayInfo;
        if (!userProfileId || userProfileId === '') {
          return;
        }
        try {
          userWorkoutSessionWithDisplayInfo.uid = userProfileId;
          userWorkoutSessionWithDisplayInfo.startZone =
            userWorkoutSessionWithDisplayInfo.exerciseSessions[0].zone.numberInGym;
          userWorkoutSessionWithDisplayInfo.isPastWorkout =
            moment(userWorkoutSessionWithDisplayInfo.startTime).format() < moment(new Date()).format();
          const userProfile = userProfileMap[userProfileId];
          userWorkoutSessionWithDisplayInfo.name = userProfile.nickname
            ? userProfile.nickname
            : userProfile.firstname;
          const workoutSessionStatus =
            UserWorkoutSessionWithDisplayInfo.getUserWorkoutStatus(
              userProfile);
          const isNewUser = workoutSessionStatus === UserWorkoutStatus.NewUser ||
            workoutSessionStatus === UserWorkoutStatus.NewMinorUser;
          userWorkoutSessionWithDisplayInfo.userProfile = {...userProfile,
            acceptedTermsAndConditions:
            UserWorkoutSessionWithDisplayInfo.hasAcceptedTermsAndConditions(userProfile),
          };
          userWorkoutSessionWithDisplayInfo.fullname = userProfile.fullname;
          userWorkoutSessionWithDisplayInfo.nickname = userProfile.nickname;
          userWorkoutSessionWithDisplayInfo.bookingSource =
            userWorkoutSessionWithDisplayInfo.bookingSource;
          userWorkoutSessionWithDisplayInfo.mobileNumber =
            userProfile.mobileNumber;
          userWorkoutSessionWithDisplayInfo.heartRateMonitorIdentifier =
            userProfile.heartRateMonitorIdentifier;
          userWorkoutSessionWithDisplayInfo.ptApprovedAt =
            userProfile.ptApprovedAt;
          userWorkoutSessionWithDisplayInfo.currentPlanId =
            userProfile.currentPlanId;
          userWorkoutSessionWithDisplayInfo.currentPlanStatus =
            userProfile.currentPlanStatus;
          userWorkoutSessionWithDisplayInfo.dateOfBirth =
            userProfile.dateOfBirth;
          userWorkoutSessionWithDisplayInfo.gender = userProfile.gender;
          userWorkoutSessionWithDisplayInfo.weight = userProfile.weight;
          userWorkoutSessionWithDisplayInfo.height = userProfile.height;
          userWorkoutSessionWithDisplayInfo.guardianInfo = userProfile.guardianInfo;
          userWorkoutSessionWithDisplayInfo.signupSource = userProfile.signupSource;
          userWorkoutSessionWithDisplayInfo.isNewUser = isNewUser;
          userWorkoutSessionWithDisplayInfo.longTermGoal = userProfile.longTermGoal;
          userWorkoutSessionWithDisplayInfo.skillLevel = userProfile.skillLevel;
          userWorkoutSessionWithDisplayInfo.continueSignupLink = userProfile.continueSignupLink;
          userWorkoutSessionWithDisplayInfo.email = userProfile.email;
          userWorkoutSessionWithDisplayInfo.strengthRatioForMuscleGroups = userProfile.strengthRatioForMuscleGroups;
          userWorkoutSessionWithDisplayInfo.leadStatus = userProfile.leadStatus;
          userWorkoutSessionWithDisplayInfo.userWorkoutStatus = UserWorkoutSessionWithDisplayInfo.getUserWorkoutStatus(
            userWorkoutSessionWithDisplayInfo);
          userWorkoutSessionWithDisplayInfo.acceptedTermsAndConditions =
              userProfile ? UserWorkoutSessionWithDisplayInfo.hasAcceptedTermsAndConditions(userProfile) : false;
        } catch (err) {
          this.logger.warn(`DbWorkoutWithDisplayInfoRepository: error mapping ${userProfileId} ${err}`);
          return;
        }
        userWorkoutSessionWithDisplayInfo.avatar = '';
      });
  }
}
